import React, { useCallback, useMemo, useState } from "react";
import { FiRefreshCcw, FiFilter } from "react-icons/fi";
import Divider from "../../../../components/shared/Divider/Divider";
import { Pagination } from "../../../../components/shared/Pagination/Pagination";
import Button from "../../../../components/shared/Button/Button";
import SelectMulti from "../../../../components/shared/Select/SelectMulti";
import { useAdminSearchStore } from "../../store/adminSearchStore";
import { useAdminSearchSubscriptions } from "../../hooks/useAdminSearchSubscriptions";
import { SubscriptionsFiltersPanel } from "./SubscriptionsFiltersPanel";
import { createSubscriptionsColumnDefinitions } from "./SubscriptionTableColumns";
import {
  SUBSCRIPTIONS_FIXED_COLUMNS,
  subscriptionsColumnOptions,
} from "./constants/columns";
import EditSubscriptionModal from "./EditSubscriptionModal";
import { useInvalidate } from "../../../../hooks/useInvalidate";

export default function SubscriptionsView() {
  const invalidate = useInvalidate();
  const {
    subscriptionsState,
    setSubscriptionsPagination,
    setSubscriptionsVisibleColumns,
    setSubscriptionsFiltersOpen,
    setSelectedSubscriptionId,
  } = useAdminSearchStore();

  const {
    filters,
    pagination,
    visibleColumns,
    filtersOpen,
    selectedSubscriptionId,
  } = subscriptionsState;

  const { data, isLoading, error } = useAdminSearchSubscriptions();
  const subscriptions = data?.subscriptions || [];
  const total = data?.total || 0;
  const [isEditingModalOpen, setIsEditingModalOpen] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / (pagination.limit || 1))),
    [total, pagination.limit]
  );

  const handleFiltersSubmit = useCallback(() => {
    setSubscriptionsPagination({ page: 1 });
    setSubscriptionsFiltersOpen(false);
    invalidate(["adminSearchSubscriptions_key"]);
  }, [invalidate, setSubscriptionsFiltersOpen, setSubscriptionsPagination]);

  const onPageChange = useCallback(
    async (page) => {
      const clamped = Math.min(Math.max(page, 1), totalPages);
      setSubscriptionsPagination({ page: clamped });
    },
    [totalPages, setSubscriptionsPagination]
  );

  const onRowsPerPageChange = useCallback(
    async (n) => {
      setSubscriptionsPagination({ page: 1, limit: n });
    },
    [setSubscriptionsPagination]
  );

  const onRefresh = useCallback(() => {
    invalidate(["adminSearchSubscriptions_key"]);
  }, [invalidate]);

  const handleEditSubscription = useCallback((subscriptionId) => {
    setIsEditingModalOpen(true);
    setSelectedSubscriptionId(subscriptionId);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setIsEditingModalOpen(false);
    setSelectedSubscriptionId(null);
  }, []);

  // Column definitions
  const columnDefinitions = useMemo(
    () => createSubscriptionsColumnDefinitions(),
    []
  );

  const columnHandlers = useMemo(
    () => ({
      onEditSubscription: handleEditSubscription,
    }),
    [handleEditSubscription]
  );

  const visibleColumnDefs = useMemo(() => {
    // Always include the fixed columns first
    const fixedCols = SUBSCRIPTIONS_FIXED_COLUMNS.map(
      (colKey) => columnDefinitions[colKey]
    ).filter(Boolean);
    const otherCols = visibleColumns
      .filter(
        (colKey) =>
          !SUBSCRIPTIONS_FIXED_COLUMNS.includes(colKey) &&
          columnDefinitions[colKey]
      )
      .map((colKey) => columnDefinitions[colKey]);

    return [...fixedCols, ...otherCols];
  }, [visibleColumns, columnDefinitions]);

  const columnCount = visibleColumnDefs.length || 1;

  const hasActiveFilters = useMemo(() => {
    const {
      userInput,
      autoRenewal,
      freeTrialUsed,
      startDate,
      endDate,
      lastUpdated,
      subscriptionType,
    } = filters;
    const hasPlan =
      Array.isArray(subscriptionType) && subscriptionType.length > 0;
    const hasDates =
      (startDate?.from || startDate?.to || endDate?.from || endDate?.to) ??
      false;
    const hasUpdated = lastUpdated?.from || lastUpdated?.to;
    return Boolean(
      (userInput || "").trim() ||
        typeof autoRenewal === "boolean" ||
        typeof freeTrialUsed === "boolean" ||
        hasPlan ||
        hasDates ||
        hasUpdated
    );
  }, [filters]);

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          onClick={() => setSubscriptionsFiltersOpen(true)}
          color={hasActiveFilters ? "primary50" : "white/gray300"}
          size="md"
          rounded="lg"
          startContent={<FiFilter />}
          title="Open filters"
        >
          Filters
        </Button>
        <SelectMulti
          header="Columns"
          className="min-w-[192px]"
          onChange={(val) => {
            if (SUBSCRIPTIONS_FIXED_COLUMNS.includes(val)) return;
            setSubscriptionsVisibleColumns(
              (prev = SUBSCRIPTIONS_FIXED_COLUMNS) => {
                const filtered = prev.filter(
                  (v) => !SUBSCRIPTIONS_FIXED_COLUMNS.includes(v)
                );
                return filtered.includes(val)
                  ? filtered.filter((v) => v !== val)
                  : [...filtered, val];
              }
            );
          }}
          onClear={() =>
            setSubscriptionsVisibleColumns([...SUBSCRIPTIONS_FIXED_COLUMNS])
          }
          options={subscriptionsColumnOptions}
          placeholder="Select columns..."
          values={(visibleColumns || []).filter(
            (v) => !SUBSCRIPTIONS_FIXED_COLUMNS.includes(v)
          )}
          buttonProps={{
            size: "md",
            rounded: "lg",
            color:
              (visibleColumns || []).filter(
                (v) => !SUBSCRIPTIONS_FIXED_COLUMNS.includes(v)
              ).length > 0
                ? "primary50"
                : "white/gray300",
          }}
        />
        <Button
          type="button"
          isDisabled={isLoading}
          onClick={onRefresh}
          color="white/gray300"
          size="md"
          rounded="lg"
          startContent={<FiRefreshCcw />}
          title="Refresh"
        >
          Refresh
        </Button>
      </div>

      <SubscriptionsFiltersPanel
        isOpen={filtersOpen}
        onClose={() => setSubscriptionsFiltersOpen(false)}
        onSubmit={handleFiltersSubmit}
      />

      <div className="overflow-hidden rounded-xl border border-gray200">
        <div className="overflow-x-auto min-h-80">
          <table className="min-w-full text-left text-base">
            <thead className="bg-gray50 text-sm uppercase tracking-wide text-gray600">
              <tr>{visibleColumnDefs.map((col) => col.renderHeader())}</tr>
            </thead>

            <tbody className="divide-y divide-gray200">
              {error && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-6 py-10 text-center text-base text-red-700"
                  >
                    {error?.message || "Failed to fetch users"}
                  </td>
                </tr>
              )}
              {isLoading && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-6 py-10 text-center text-base text-gray500"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && subscriptions.length === 0 && !error && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-6 py-10 text-center text-base text-gray500"
                  >
                    No results
                  </td>
                </tr>
              )}

              {subscriptions.map((s, index) => {
                return (
                  <tr key={s.id} className={index % 2 === 1 ? "bg-gray50" : ""}>
                    {visibleColumnDefs.map((col) => (
                      <React.Fragment key={col.key}>
                        {col.renderCell(s, columnHandlers)}
                      </React.Fragment>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Divider color="gray200" type="horizontal" />

        <Pagination
          currentPage={pagination.page}
          pageNumber={totalPages}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPage={pagination.limit}
          showItemsPerPage={subscriptions.length > 0}
          size="lg"
          className="px-6 py-4"
        />
      </div>

      <EditSubscriptionModal
        isOpen={isEditingModalOpen}
        onClose={handleCloseEdit}
      />
    </>
  );
}
